import React, { useState } from 'react';
import './AddTeamModal.scss';
import api from '../services/apiService';

function AddTeamModal({ onClose, onTeamAdded, teamType, currentTeam }) {
  const [formData, setFormData] = useState({
    team_name: '',
    location: '',
    county: '',
    age_group: currentTeam?.age_group || '',
    league_name: currentTeam?.league_name || '',
    contact_first_name: '',
    contact_last_name: '',
    contact_email: '',
    contact_phone: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validateForm = () => {
    const newErrors = {};
    const requiredFields = ['team_name', 'location', 'county', 'contact_first_name', 'contact_last_name', 'contact_phone'];
    
    requiredFields.forEach(field => {
      if (!formData[field].trim()) {
        newErrors[field] = 'This field is required';
      }
    });

    // Validate email format (only if provided)
    if (formData.contact_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contact_email)) {
      newErrors.contact_email = 'Please enter a valid email address';
    }

    // Validate phone format (basic UK format) - required
    if (!formData.contact_phone || !/^(\+44|0)[1-9]\d{8,9}$/.test(formData.contact_phone.replace(/\s/g, ''))) {
      newErrors.contact_phone = 'Please enter a valid UK phone number';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await api.post(`${process.env.REACT_APP_API_URL}/add_team.php`, {
        ...formData,
        team_type: teamType
      });
      
      if (response.data.success) {
        if (onTeamAdded) {
          onTeamAdded({
            team_id: response.data.team_id,
            team_name: formData.team_name,
            type: teamType
          });
        }
        onClose();
      } else {
        setErrors(prev => ({
          ...prev,
          submit: response.data.message
        }));
      }
    } catch (error) {
      console.error('Error adding team:', error);
      setErrors(prev => ({
        ...prev,
        submit: 'Error adding team. Please try again.'
      }));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="add-team-modal modal-overlay">
      <div className="modal-content modal">
        <button className="modal-close" onClick={onClose}>&times;</button>
        <h2>Add New Team</h2>
        <p className="modal-description">
          Add a team to the directory for easy match scheduling. This will create a team entry and contact details for the manager.
        </p>
        
        {errors.submit && <div className="error-message">{errors.submit}</div>}
        
        <form onSubmit={handleSubmit}>
          <div className="form-section">
            <h3>Team Details</h3>
            
            <div className={`form-group ${errors.team_name ? 'has-error' : ''}`}>
              <label htmlFor="team_name">
                Team Name*
                {errors.team_name && <span className="error-text">{errors.team_name}</span>}
              </label>
              <input
                type="text"
                id="team_name"
                name="team_name"
                value={formData.team_name}
                onChange={handleChange}
                placeholder="e.g., St. Leonard's U12s"
                required
              />
            </div>

            <div className="form-row">
              <div className={`form-group ${errors.location ? 'has-error' : ''}`}>
                <label htmlFor="location">
                  Location*
                  {errors.location && <span className="error-text">{errors.location}</span>}
                </label>
                <input
                  type="text"
                  id="location"
                  name="location"
                  value={formData.location}
                  onChange={handleChange}
                  placeholder="e.g., Exeter"
                  required
                />
              </div>

              <div className={`form-group ${errors.county ? 'has-error' : ''}`}>
                <label htmlFor="county">
                  County*
                  {errors.county && <span className="error-text">{errors.county}</span>}
                </label>
                <input
                  type="text"
                  id="county"
                  name="county"
                  value={formData.county}
                  onChange={handleChange}
                  placeholder="e.g., Devon"
                  required
                />
              </div>
            </div>

            <div className="form-row">
              {teamType !== 'squad' && (
                <div className="form-group">
                  <label htmlFor="age_group">Age Group:</label>
                  <select
                    id="age_group"
                    name="age_group"
                    value={formData.age_group}
                    onChange={handleChange}
                  >
                    <option value="">Select age group</option>
                    <option value="U7s">U7s</option>
                    <option value="U8s">U8s</option>
                    <option value="U9s">U9s</option>
                    <option value="U10s">U10s</option>
                    <option value="U11s">U11s</option>
                    <option value="U12s">U12s</option>
                    <option value="U13s">U13s</option>
                    <option value="U14s">U14s</option>
                    <option value="U15s">U15s</option>
                    <option value="U16s">U16s</option>
                    <option value="U17s">U17s</option>
                    <option value="U18s">U18s</option>
                    <option value="U21s">U21s</option>
                    <option value="U23s">U23s</option>
                  </select>
                </div>
              )}

              <div className="form-group">
                <label htmlFor="league_name">League Name:</label>
                <input
                  type="text"
                  id="league_name"
                  name="league_name"
                  value={formData.league_name}
                  onChange={handleChange}
                  placeholder="e.g., Devon Youth League"
                />
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3>Contact Details</h3>
            
            <div className="form-row">
              <div className={`form-group ${errors.contact_first_name ? 'has-error' : ''}`}>
                <label htmlFor="contact_first_name">
                  First Name*
                  {errors.contact_first_name && <span className="error-text">{errors.contact_first_name}</span>}
                </label>
                <input
                  type="text"
                  id="contact_first_name"
                  name="contact_first_name"
                  value={formData.contact_first_name}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className={`form-group ${errors.contact_last_name ? 'has-error' : ''}`}>
                <label htmlFor="contact_last_name">
                  Last Name*
                  {errors.contact_last_name && <span className="error-text">{errors.contact_last_name}</span>}
                </label>
                <input
                  type="text"
                  id="contact_last_name"
                  name="contact_last_name"
                  value={formData.contact_last_name}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>

            <div className={`form-group ${errors.contact_email ? 'has-error' : ''}`}>
              <label htmlFor="contact_email">
                Email Address
                {errors.contact_email && <span className="error-text">{errors.contact_email}</span>}
              </label>
              <input
                type="email"
                id="contact_email"
                name="contact_email"
                value={formData.contact_email}
                onChange={handleChange}
                placeholder="manager@example.com"
              />
            </div>

            <div className={`form-group ${errors.contact_phone ? 'has-error' : ''}`}>
              <label htmlFor="contact_phone">
                Phone Number*
                {errors.contact_phone && <span className="error-text">{errors.contact_phone}</span>}
              </label>
              <input
                type="tel"
                id="contact_phone"
                name="contact_phone"
                value={formData.contact_phone}
                onChange={handleChange}
                placeholder="+44 7123 456789 or 07123 456789"
                maxLength="20"
                required
              />
            </div>
          </div>

          <div className="form-actions modal-buttons">
            <button 
              type="button" 
              className="cancel-button" 
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="submit-button"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Adding Team...' : 'Add Team'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AddTeamModal; 